import type { CarrierComplianceSnapshot } from "../tma";

export type CarrierOnboardingStepId = "mc" | "fmcsa" | "coi" | "w9" | "agreement";

export interface CarrierOnboardingState {
  tenantId: string;
  carrierMc?: string;
  carrierName?: string;
  dotNumber?: string;
  fmcsaSnapshot?: CarrierComplianceSnapshot;
  coiDocumentId?: string;
  w9DocumentId?: string;
  signedAgreementName?: string;
}

export interface CarrierOnboardingStep {
  id: CarrierOnboardingStepId;
  label: string;
  complete: boolean;
}

export type FmcsaVerificationState = "pending" | "verified" | "unavailable";

export function fmcsaVerificationState(
  snapshot: CarrierComplianceSnapshot | undefined,
): FmcsaVerificationState | undefined {
  const state = snapshot?.evidencePayload?.["verificationState"];
  return state === "pending" || state === "verified" || state === "unavailable" ? state : undefined;
}

/**
 * Fail-closed placeholder written before the real QCMobile lookup completes.
 * `blocked: true` keeps the FMCSA step incomplete until a genuine result
 * (or an explicit verification-unavailable state) replaces it.
 */
export function pendingFmcsaOnboardingSnapshot(args: {
  tenantId: string;
  carrierMc: string;
  carrierName?: string;
  checkedAt?: string;
}): CarrierComplianceSnapshot {
  return {
    tenantId: args.tenantId,
    carrierMc: args.carrierMc,
    carrierName: args.carrierName,
    provider: "fmcsa",
    checkedAt: args.checkedAt ?? new Date().toISOString(),
    authorityStatus: "unknown",
    insuranceStatus: "unknown",
    safetyRating: "unknown",
    fraudIndicators: ["verification_pending"],
    blocked: true,
    riskScore: 50,
    evidencePayload: { verificationState: "pending" satisfies FmcsaVerificationState, provider: "fmcsa" },
  };
}

/**
 * @deprecated No longer fabricates FMCSA data. Kept for the local (no-Convex)
 * portal preview, where it now returns the fail-closed pending snapshot; the
 * real lookup runs server-side via convex/fmcsaVerification.
 */
export function mockFmcsaOnboardingLookup(args: {
  tenantId: string;
  carrierMc: string;
  carrierName?: string;
}) {
  return pendingFmcsaOnboardingSnapshot(args);
}

export function buildCarrierOnboardingProgress(state: CarrierOnboardingState) {
  const steps: CarrierOnboardingStep[] = [
    { id: "mc", label: "MC entered", complete: !!state.carrierMc },
    { id: "fmcsa", label: "FMCSA checked", complete: !!state.fmcsaSnapshot && !state.fmcsaSnapshot.blocked },
    { id: "coi", label: "COI uploaded", complete: !!state.coiDocumentId },
    { id: "w9", label: "W9 uploaded", complete: !!state.w9DocumentId },
    { id: "agreement", label: "Agreement signed", complete: !!state.signedAgreementName },
  ];
  return {
    steps,
    complete: steps.every((step) => step.complete),
    blockedReasons: blockedReasons(state),
  };
}

export function buildVerifyCarrierAgentDraft(state: CarrierOnboardingState) {
  const progress = buildCarrierOnboardingProgress(state);
  return {
    action: "verify_carrier_onboarding",
    draftOnly: true,
    ready: progress.complete,
    carrierMc: state.carrierMc,
    carrierName: state.carrierName,
    dotNumber: state.dotNumber,
    evidence: {
      fmcsa: state.fmcsaSnapshot?.evidencePayload,
      coiDocumentId: state.coiDocumentId,
      w9DocumentId: state.w9DocumentId,
      signedAgreementName: state.signedAgreementName,
    },
    blockedReasons: progress.blockedReasons,
  };
}

function blockedReasons(state: CarrierOnboardingState) {
  const reasons: string[] = [];
  if (!state.carrierMc) reasons.push("MC number required");
  if (!state.fmcsaSnapshot) {
    reasons.push("FMCSA lookup required");
  } else {
    const verification = fmcsaVerificationState(state.fmcsaSnapshot);
    if (verification === "pending") {
      reasons.push("FMCSA verification pending");
    } else if (verification === "unavailable") {
      reasons.push("FMCSA verification unavailable — pending manual review");
    } else if (state.fmcsaSnapshot.blocked) {
      reasons.push("FMCSA authority or insurance blocks onboarding");
    }
  }
  if (!state.coiDocumentId) reasons.push("COI upload required");
  if (!state.w9DocumentId) reasons.push("W9 upload required");
  if (!state.signedAgreementName) reasons.push("Broker agreement signature required");
  return reasons;
}
